import * as fabric from "fabric";
import { LayoutPreset, TGuideLines } from "../presets/layoutPresets";
import { stageDimensions } from "../constants/common";
import useRecorderContext from "../context/useRecorderContext";
import { useCallback, useRef, useEffect } from "react";

interface IUseGuideLineHook {
  guideLinesRef: React.MutableRefObject<TGuideLines>;
  interactingRef: React.MutableRefObject<boolean>;
  getCurrentLayout: () => { stableLayout: LayoutPreset; isRecording: boolean };
  streamType?: "screen" | "webcam" | null;
}

const useGuideLineHook = ({
  guideLinesRef,
  interactingRef,
  getCurrentLayout,
  streamType,
}: IUseGuideLineHook) => {
  const {
    showGrid,
    isRecording,
    showCountdown,
    canvasWidth,
    canvasHeight,
    canvasRatio,
  } = useRecorderContext();

  // Use a ref to store the current showGrid value to avoid stale closure issues
  const showGridRef = useRef(showGrid);
  const showCountDownRef = useRef(showCountdown);

  useEffect(() => {
    showGridRef.current = showGrid;
    showCountDownRef.current = showCountdown;
  }, [showGrid, showCountdown]);

  // Get consistent stroke width across all ratios and qualities
  const getAdjustedStrokeWidth = useCallback((baseStrokeWidth: number) => {
    // Use a base reference size for consistent scaling across all ratios
    // Base reference: 1080p 16:9 (1920x1080)
    const baseWidth = 1920;
    const baseHeight = 1080;

    let scale;
    if (canvasRatio === "9:16") {
      // For 9:16 ratio, use height as the reference dimension
      scale = window.outerWidth / baseHeight;
    } else {
      // For 16:9 and 1:1 ratios, use width as the reference dimension
      scale = canvasWidth / baseWidth;
    }
    // Scale should increase with higher quality (larger canvas)
    return Math.max(0.5, baseStrokeWidth * scale);
  }, []);

  const snapIt = ({
    snapX,
    snapY,
    frameLeft,
    frameTop,
    frame,
    canvas,
    callback,
  }: {
    snapX: number;
    snapY: number;
    frameLeft: number;
    frameTop: number;
    frame: fabric.Rect;
    canvas: fabric.Canvas;
    callback: any;
  }) => {
    if (snapX !== null || snapY !== null) {
      const newX = snapX !== null ? snapX : frameLeft;
      const newY = snapY !== null ? snapY : frameTop;

      frame.set({
        left: newX,
        top: newY,
      });

      // Set coordinates directly for immediate update
      frame.setCoords();
      callback();

      // Force canvas re-render
      // canvas.requestRenderAll();
    }
  };

  // Helper to update a single guide line
  const updateSingleGuide = useCallback(
    (
      canvas: fabric.Canvas,
      guide: fabric.Line | null,
      shouldShow: boolean,
      position: number,
      isHorizontal: boolean = false
    ) => {
      if (!guide) return;

      // Check if guide exists in canvas
      const guideExists = canvas.getObjects().includes(guide);

      if (shouldShow) {
        // Update guide position based on current canvas dimensions
        if (isHorizontal) {
          guide.set({
            x1: 0,
            y1: position,
            x2: canvas.getWidth(),
            y2: position,
          });
        } else {
          guide.set({
            x1: position,
            y1: 0,
            x2: position,
            y2: canvas.getHeight(),
          });
        }

        if (!guideExists) {
          canvas.add(guide);
        }
        // Always bring guide to front when showing, regardless of whether it was just added
        canvas.bringObjectToFront(guide);
      } else if (guideExists) {
        canvas.remove(guide);
      }
    },
    []
  );
  // Helper to update object alignment guides based on stableLayout
  const updateObjectAlignmentGuides = useCallback(
    (canvas: fabric.Canvas, frame: fabric.Rect, callback: any) => {
      const { stableLayout } = getCurrentLayout();
      if (!stableLayout || stableLayout.length <= 1) return;

      const frameLeft = frame.getX();
      const frameTop = frame.getY();
      const frameRight = frameLeft + (frame.width ?? 0) * (frame.scaleX ?? 1);
      const frameBottom = frameTop + (frame.height ?? 0) * (frame.scaleY ?? 1);
      const frameCenterX = frameLeft + (frameRight - frameLeft) / 2;
      const frameCenterY = frameTop + (frameBottom - frameTop) / 2;

      const tolerance = 20; // pixels of tolerance for showing object alignment guides
      const snapTolerance = 10; // pixels of tolerance for snapping to object alignment (10px close to line)

      // Find other objects in stable layout (excluding current stream)
      const otherObjects = stableLayout.filter(
        (layout) => layout.stream !== streamType
      );
      if (otherObjects.length === 0) return;

      // Check alignment with other objects
      let showObjectLeft = false;
      let showObjectRight = false;
      let showObjectTop = false;
      let showObjectBottom = false;
      let showObjectCenterX = false;
      let showObjectCenterY = false;

      let objectLeftPos = 0;
      let objectRightPos = 0;
      let objectTopPos = 0;
      let objectBottomPos = 0;
      let objectCenterXPos = 0;
      let objectCenterYPos = 0;

      // Variables to track snapping
      let snapX: number | null = null;
      let snapY: number | null = null;

      for (const obj of otherObjects) {
        const objLeft = obj.x;
        const objTop = obj.y;
        const objRight = obj.x + obj.width;
        const objBottom = obj.y + obj.height;
        const objCenterX = obj.x + obj.width / 2;
        const objCenterY = obj.y + obj.height / 2;

        // Check left edge alignment
        if (Math.abs(frameLeft - objLeft) < tolerance) {
          showObjectLeft = true;
          objectLeftPos = objLeft;
          // Only snap if very close (1px tolerance)
          if (Math.abs(frameLeft - objLeft) <= snapTolerance) {
            snapX = objLeft; // Snap to object's left edge
          }
        }
        if (Math.abs(frameLeft - objRight) < tolerance) {
          showObjectLeft = true;
          objectLeftPos = objRight;
          // Only snap if very close (1px tolerance)
          if (Math.abs(frameLeft - objRight) <= snapTolerance) {
            snapX = objRight; // Snap to object's right edge
          }
        }
        // NEW: Check if frame left edge aligns with object center X
        if (Math.abs(frameLeft - objCenterX) < tolerance) {
          showObjectLeft = true;
          objectLeftPos = objCenterX;
          // Only snap if very close (1px tolerance)
          if (Math.abs(frameLeft - objCenterX) <= snapTolerance) {
            snapX = objCenterX; // Snap to object's center X
          }
        }

        // Check right edge alignment
        if (Math.abs(frameRight - objLeft) < tolerance) {
          showObjectRight = true;
          objectRightPos = objLeft;
          // Only snap if very close (1px tolerance)
          if (Math.abs(frameRight - objLeft) <= snapTolerance) {
            snapX = objLeft - (frame.width ?? 0) * (frame.scaleX ?? 1); // Snap so right edge aligns with object's left edge
          }
        }
        if (Math.abs(frameRight - objRight) < tolerance) {
          showObjectRight = true;
          objectRightPos = objRight;
          // Only snap if very close (1px tolerance)
          if (Math.abs(frameRight - objRight) <= snapTolerance) {
            snapX = objRight - (frame.width ?? 0) * (frame.scaleX ?? 1); // Snap so right edge aligns with object's right edge
          }
        }
        // NEW: Check if frame right edge aligns with object center X
        if (Math.abs(frameRight - objCenterX) < tolerance) {
          showObjectRight = true;
          objectRightPos = objCenterX;
          // Only snap if very close (1px tolerance)
          if (Math.abs(frameRight - objCenterX) <= snapTolerance) {
            snapX = objCenterX - (frame.width ?? 0) * (frame.scaleX ?? 1); // Snap so right edge aligns with object's center X
          }
        }

        // Check top edge alignment
        if (Math.abs(frameTop - objTop) < tolerance) {
          showObjectTop = true;
          objectTopPos = objTop;
          // Only snap if very close (1px tolerance)
          if (Math.abs(frameTop - objTop) <= snapTolerance) {
            snapY = objTop; // Snap to object's top edge
          }
        }
        if (Math.abs(frameTop - objBottom) < tolerance) {
          showObjectTop = true;
          objectTopPos = objBottom;
          // Only snap if very close (1px tolerance)
          if (Math.abs(frameTop - objBottom) <= snapTolerance) {
            snapY = objBottom; // Snap to object's bottom edge
          }
        }
        // NEW: Check if frame top edge aligns with object center Y
        if (Math.abs(frameTop - objCenterY) < tolerance) {
          showObjectTop = true;
          objectTopPos = objCenterY;
          // Only snap if very close (1px tolerance)
          if (Math.abs(frameTop - objCenterY) <= snapTolerance) {
            snapY = objCenterY; // Snap to object's center Y
          }
        }

        // Check bottom edge alignment
        if (Math.abs(frameBottom - objTop) < tolerance) {
          showObjectBottom = true;
          objectBottomPos = objTop;
          // Only snap if very close (1px tolerance)
          if (Math.abs(frameBottom - objTop) <= snapTolerance) {
            snapY = objTop - (frame.height ?? 0) * (frame.scaleY ?? 1); // Snap so bottom edge aligns with object's top edge
          }
        }
        if (Math.abs(frameBottom - objBottom) < tolerance) {
          showObjectBottom = true;
          objectBottomPos = objBottom;
          // Only snap if very close (1px tolerance)
          if (Math.abs(frameBottom - objBottom) <= snapTolerance) {
            snapY = objBottom - (frame.height ?? 0) * (frame.scaleY ?? 1); // Snap so bottom edge aligns with object's bottom edge
          }
        }
        // NEW: Check if frame bottom edge aligns with object center Y
        if (Math.abs(frameBottom - objCenterY) < tolerance) {
          showObjectBottom = true;
          objectBottomPos = objCenterY;
          // Only snap if very close (1px tolerance)
          if (Math.abs(frameBottom - objCenterY) <= snapTolerance) {
            snapY = objCenterY - (frame.height ?? 0) * (frame.scaleY ?? 1); // Snap so bottom edge aligns with object's center Y
          }
        }

        // Check center alignment (existing)
        if (Math.abs(frameCenterX - objCenterX) < tolerance) {
          showObjectCenterX = true;
          objectCenterXPos = objCenterX;
          // Only snap if very close (1px tolerance)
          if (Math.abs(frameCenterX - objCenterX) <= snapTolerance) {
            snapX = objCenterX - ((frame.width ?? 0) * (frame.scaleX ?? 1)) / 2; // Snap center to center
          }
        }
        if (Math.abs(frameCenterY - objCenterY) < tolerance) {
          showObjectCenterY = true;
          objectCenterYPos = objCenterY;
          // Only snap if very close (1px tolerance)
          if (Math.abs(frameCenterY - objCenterY) <= snapTolerance) {
            snapY =
              objCenterY - ((frame.height ?? 0) * (frame.scaleY ?? 1)) / 2; // Snap center to center
          }
        }
      }

      // Apply snapping if within tolerance
      if (snapX !== null || snapY !== null) {
        const newX = snapX !== null ? snapX : frameLeft;
        const newY = snapY !== null ? snapY : frameTop;

        frame.set({
          left: newX,
          top: newY,
        });

        // Set coordinates directly for immediate update
        frame.setCoords();
        callback();
        // Force canvas re-render
        canvas.requestRenderAll();
      }

      // Only show guides if currently interacting
      const shouldShowGuides = interactingRef.current;

      updateSingleGuide(
        canvas,
        guideLinesRef.current.objectLeft,
        shouldShowGuides && showObjectLeft,
        objectLeftPos,
        false
      );
      updateSingleGuide(
        canvas,
        guideLinesRef.current.objectRight,
        shouldShowGuides && showObjectRight,
        objectRightPos,
        false
      );
      updateSingleGuide(
        canvas,
        guideLinesRef.current.objectTop,
        shouldShowGuides && showObjectTop,
        objectTopPos,
        true
      );
      updateSingleGuide(
        canvas,
        guideLinesRef.current.objectBottom,
        shouldShowGuides && showObjectBottom,
        objectBottomPos,
        true
      );
      updateSingleGuide(
        canvas,
        guideLinesRef.current.objectCenterX,
        shouldShowGuides && showObjectCenterX,
        objectCenterXPos,
        false
      );
      updateSingleGuide(
        canvas,
        guideLinesRef.current.objectCenterY,
        shouldShowGuides && showObjectCenterY,
        objectCenterYPos,
        true
      );
    },
    [
      getCurrentLayout,
      guideLinesRef,
      interactingRef,
      streamType,
      updateSingleGuide,
    ]
  );

  // Helper to check if frame is near guide positions and update guide visibility
  const updateGuideVisibility = useCallback(
    (canvas: fabric.Canvas, frame: fabric.Rect, callback: any) => {
      // Hide guides during countdown, but allow them during recording when interacting
      if (!showGridRef.current || showCountDownRef.current) {
        return;
      }

      if (
        !guideLinesRef.current.left ||
        !guideLinesRef.current.right ||
        !guideLinesRef.current.top ||
        !guideLinesRef.current.bottom ||
        !guideLinesRef.current.centerX ||
        !guideLinesRef.current.centerY ||
        !guideLinesRef.current.objectLeft ||
        !guideLinesRef.current.objectRight ||
        !guideLinesRef.current.objectTop ||
        !guideLinesRef.current.objectBottom ||
        !guideLinesRef.current.objectCenterX ||
        !guideLinesRef.current.objectCenterY ||
        !guideLinesRef.current.leftEdge ||
        !guideLinesRef.current.rightEdge ||
        !guideLinesRef.current.topEdge ||
        !guideLinesRef.current.bottomEdge
      )
        return;

      // Use actual canvas dimensions instead of container dimensions
      const canvasWidth = canvas.getWidth();
      const canvasHeight = canvas.getHeight();
      const frameLeft = frame.getX();
      const frameRight = canvasWidth - (frameLeft + (frame.width ?? 0));
      const frameTop = frame.getY();
      const frameBottom = canvasHeight - (frameTop + (frame.height ?? 0));
      const frameRightXY = frameLeft + (frame.width ?? 0) * (frame.scaleX ?? 1);
      const frameBottomXY =
        frameTop + (frame.height ?? 0) * (frame.scaleY ?? 1);
      const frameCenterX = frameLeft + (frameRightXY - frameLeft) / 2;
      const frameCenterY = frameTop + (frameBottomXY - frameTop) / 2;
      const canvasCenterX = canvasWidth / 2;
      const canvasCenterY = canvasHeight / 2;

      // Variables to track snapping
      let snapX: number | null = null;
      let snapY: number | null = null;
      const toleranceMin = 32; // pixels of tolerance for showing guides
      const toleranceMax = 40; // pixels of tolerance for showing guides
      const tolerance = 20; // pixels of tolerance for center guides
      const snapTolerance = 10; // pixels of tolerance for snapping (10px close to line)

      // Check left edge near canvas left
      const isLeftEdgeNearLeft =
        frameLeft > toleranceMin && frameLeft <= toleranceMax;
      // Check right edge near canvas left (when frame is very close to left edge)
      const isRightEdgeNearLeft =
        frameLeft < toleranceMax && frameLeft >= -toleranceMin;
      const showLeftGuide = isLeftEdgeNearLeft || isRightEdgeNearLeft;

      // Check if close enough to snap to left edge (1px tolerance)
      const isLeftEdgeSnapClose =
        Math.abs(frameLeft - toleranceMin) <= snapTolerance;
      if (isLeftEdgeSnapClose) {
        snapX = toleranceMin;
        snapIt({
          snapX,
          snapY: frameTop,
          frameLeft,
          frameTop,
          frame,
          canvas,
          callback,
        });
      }

      // Check right edge near canvas right
      const isRightEdgeNearRight =
        frameRightXY > canvasWidth - toleranceMax &&
        frameRightXY <= canvasWidth - toleranceMin;
      // Check left edge near canvas right (when frame is very close to right edge)
      const isLeftEdgeNearRight =
        frameLeft > canvasWidth - toleranceMax &&
        frameLeft <= canvasWidth - toleranceMin;
      const showRightGuide = isRightEdgeNearRight || isLeftEdgeNearRight;

      // Check if close enough to snap to right edge (1px tolerance)
      const rightEdgePosition = canvasWidth - toleranceMin;
      const isRightEdgeSnapClose =
        Math.abs(
          frameLeft -
            (rightEdgePosition - (frame.width ?? 0) * (frame.scaleX ?? 1))
        ) <= snapTolerance;
      if (isRightEdgeSnapClose) {
        snapX = rightEdgePosition - (frame.width ?? 0) * (frame.scaleX ?? 1);
        snapIt({
          snapX,
          snapY: frameTop,
          frameLeft,
          frameTop,
          frame,
          canvas,
          callback,
        });
      }
      // Check top edge near canvas top
      const isTopEdgeNearTop =
        frameTop > toleranceMin && frameTop <= toleranceMax;
      // Check bottom edge near canvas top (when frame is very close to top edge)
      const isBottomEdgeNearTop =
        frameTop < toleranceMax && frameTop >= -toleranceMin;
      const showTopGuide = isTopEdgeNearTop || isBottomEdgeNearTop;

      // Check if close enough to snap to top edge (1px tolerance)
      const isTopEdgeSnapClose =
        Math.abs(frameTop - toleranceMin) <= snapTolerance;
      if (isTopEdgeSnapClose) {
        snapY = toleranceMin;
        snapIt({
          snapX: snapX || frameLeft,
          snapY: snapY,
          frameLeft,
          frameTop,
          frame,
          canvas,
          callback,
        });
      }

      // Check bottom edge near canvas bottom
      const isBottomEdgeNearBottom =
        frameBottomXY > canvasHeight - toleranceMax &&
        frameBottomXY <= canvasHeight - toleranceMin;
      // Check top edge near canvas bottom (when frame is very close to bottom edge)
      const isTopEdgeNearBottom =
        frameTop > canvasHeight - toleranceMax &&
        frameTop <= canvasHeight - toleranceMin;
      const showBottomGuide = isBottomEdgeNearBottom || isTopEdgeNearBottom;

      // Check if close enough to snap to bottom edge (1px tolerance)
      const bottomEdgePosition = canvasHeight - toleranceMin;
      const isBottomEdgeSnapClose =
        Math.abs(
          frameTop -
            (bottomEdgePosition - (frame.height ?? 0) * (frame.scaleY ?? 1))
        ) <= snapTolerance;
      if (isBottomEdgeSnapClose) {
        snapY = bottomEdgePosition - (frame.height ?? 0) * (frame.scaleY ?? 1);
        snapIt({
          snapX: snapX || frameLeft,
          snapY: snapY,
          frameLeft,
          frameTop,
          frame,
          canvas,
          callback,
        });
      }
      // Check center X
      const isNearCenterX = Math.abs(frameCenterX - canvasCenterX) < tolerance;
      const isLeftEdgeNearCenterX =
        frameLeft <= canvasCenterX + tolerance && frameLeft >= canvasCenterX;
      const isRightEdgeNearCenterX =
        frameRight <= canvasCenterX + tolerance && frameRight >= canvasCenterX;
      const showCenterXGuide =
        isNearCenterX || isLeftEdgeNearCenterX || isRightEdgeNearCenterX;

      // Check center Y
      const isNearCenterY = Math.abs(frameCenterY - canvasCenterY) < tolerance;
      const isTopEdgerNearCenterY =
        frameTop <= canvasCenterY + tolerance && frameTop >= canvasCenterY;
      const isBottomEdgerNearCenterY =
        frameBottom >= canvasCenterY &&
        frameBottom <= canvasCenterY + tolerance;
      const showCenterYGuide =
        isNearCenterY || isTopEdgerNearCenterY || isBottomEdgerNearCenterY;

      // Check if close enough to snap to center X (1px tolerance)
      const isCenterXSnapClose =
        Math.abs(frameCenterX - canvasCenterX) <= snapTolerance;
      if (isCenterXSnapClose) {
        const frameWidth = (frame.width ?? 0) * (frame.scaleX ?? 1);
        snapX = canvasCenterX - frameWidth / 2;
        snapIt({
          snapX,
          snapY: snapY || frameTop,
          frameLeft,
          frameTop,
          frame,
          canvas,
          callback,
        });
      }

      // Check if left edge is close enough to snap to center X (1px tolerance)
      const isLeftEdgeCenterXSnapClose =
        Math.abs(frameLeft - canvasCenterX) <= snapTolerance;
      if (isLeftEdgeCenterXSnapClose) {
        snapX = canvasCenterX;
        snapIt({
          snapX,
          snapY: snapY || frameTop,
          frameLeft,
          frameTop,
          frame,
          canvas,
          callback,
        });
      }

      // Check if right edge is close enough to snap to center X (1px tolerance)
      const isRightEdgeCenterXSnapClose =
        Math.abs(frameRightXY - canvasCenterX) <= snapTolerance;
      if (isRightEdgeCenterXSnapClose) {
        const frameWidth = (frame.width ?? 0) * (frame.scaleX ?? 1);
        snapX = canvasCenterX - frameWidth;
        snapIt({
          snapX,
          snapY: snapY || frameTop,
          frameLeft,
          frameTop,
          frame,
          canvas,
          callback,
        });
      }

      // Check if close enough to snap to center Y (1px tolerance)
      const isCenterYSnapClose =
        Math.abs(frameCenterY - canvasCenterY) <= snapTolerance;
      if (isCenterYSnapClose) {
        const frameHeight = (frame.height ?? 0) * (frame.scaleY ?? 1);
        snapY = canvasCenterY - frameHeight / 2;
        snapIt({
          snapX: snapX || frameLeft,
          snapY,
          frameLeft,
          frameTop,
          frame,
          canvas,
          callback,
        });
      }

      // Check if top edge is close enough to snap to center Y (1px tolerance)
      const isTopEdgeCenterYSnapClose =
        Math.abs(frameTop - canvasCenterY) <= snapTolerance;
      if (isTopEdgeCenterYSnapClose) {
        snapY = canvasCenterY;
        snapIt({
          snapX: snapX || frameLeft,
          snapY,
          frameLeft,
          frameTop,
          frame,
          canvas,
          callback,
        });
      }

      // Check if bottom edge is close enough to snap to center Y (1px tolerance)
      const isBottomEdgeCenterYSnapClose =
        Math.abs(frameBottomXY - canvasCenterY) <= snapTolerance;
      if (isBottomEdgeCenterYSnapClose) {
        const frameHeight = (frame.height ?? 0) * (frame.scaleY ?? 1);
        snapY = canvasCenterY - frameHeight;
        snapIt({
          snapX: snapX || frameLeft,
          snapY,
          frameLeft,
          frameTop,
          frame,
          canvas,
          callback,
        });
      }
      // Only show guides if currently interacting
      const shouldShowGuides = interactingRef.current;

      updateSingleGuide(
        canvas,
        guideLinesRef.current.left,
        shouldShowGuides && showLeftGuide,
        32
      );
      updateSingleGuide(
        canvas,
        guideLinesRef.current.right,
        shouldShowGuides && showRightGuide,
        canvasWidth - 32
      );
      updateSingleGuide(
        canvas,
        guideLinesRef.current.top,
        shouldShowGuides && showTopGuide,
        32,
        true
      );
      updateSingleGuide(
        canvas,
        guideLinesRef.current.bottom,
        shouldShowGuides && showBottomGuide,
        canvasHeight - 32,
        true
      );
      updateSingleGuide(
        canvas,
        guideLinesRef.current.centerX,
        shouldShowGuides && showCenterXGuide,
        canvasCenterX
      );
      updateSingleGuide(
        canvas,
        guideLinesRef.current.centerY,
        shouldShowGuides && showCenterYGuide,
        canvasCenterY,
        true
      );

      // Check if frame is close to canvas edges for snapping (1px tolerance)
      const isOnLeftEdge = frameLeft >= -1 && frameLeft <= 10;
      const isOnRightEdge = frameRight >= -1 && frameRight <= 10;
      const isOnTopEdge = frameTop >= -1 && frameTop <= 10;
      const isOnBottomEdge = frameBottom >= -1 && frameBottom <= 10;

      // Check if close enough to snap to canvas edges (1px tolerance)
      const isCanvasLeftEdgeSnapClose = Math.abs(frameLeft) <= snapTolerance;
      if (isCanvasLeftEdgeSnapClose) {
        snapX = 0;
        snapIt({
          snapX,
          snapY: snapY || frameTop,
          frameLeft,
          frameTop,
          frame,
          canvas,
          callback,
        });
      }

      const isCanvasRightEdgeSnapClose =
        Math.abs(frameRightXY - canvasWidth) <= snapTolerance;
      if (isCanvasRightEdgeSnapClose) {
        snapX = canvasWidth - (frame.width ?? 0) * (frame.scaleX ?? 1);
        snapIt({
          snapX,
          snapY: snapY || frameTop,
          frameLeft,
          frameTop,
          frame,
          canvas,
          callback,
        });
      }

      const isCanvasTopEdgeSnapClose = Math.abs(frameTop) <= snapTolerance;
      if (isCanvasTopEdgeSnapClose) {
        snapY = 0;
        snapIt({
          snapX: snapX || frameLeft,
          snapY,
          frameLeft,
          frameTop,
          frame,
          canvas,
          callback,
        });
      }

      const isCanvasBottomEdgeSnapClose =
        Math.abs(frameBottomXY - canvasHeight) <= snapTolerance;
      if (isCanvasBottomEdgeSnapClose) {
        snapY = canvasHeight - (frame.height ?? 0) * (frame.scaleY ?? 1);
        snapIt({
          snapX: snapX || frameLeft,
          snapY,
          frameLeft,
          frameTop,
          frame,
          canvas,
          callback,
        });
      }
      // Update guides for all edges
      updateSingleGuide(
        canvas,
        guideLinesRef.current.leftEdge,
        shouldShowGuides && isOnLeftEdge,
        0 // Left edge position
      );
      updateSingleGuide(
        canvas,
        guideLinesRef.current.rightEdge,
        shouldShowGuides && isOnRightEdge,
        canvasWidth
      );
      updateSingleGuide(
        canvas,
        guideLinesRef.current.topEdge,
        shouldShowGuides && isOnTopEdge,
        0, // Top edge position
        true // Horizontal guide
      );
      updateSingleGuide(
        canvas,
        guideLinesRef.current.bottomEdge,
        shouldShowGuides && isOnBottomEdge,
        canvasHeight,
        true // Horizontal guide
      );

      // Update object-to-object guide visibility
      updateObjectAlignmentGuides(canvas, frame, callback);

      // Ensure all visible guides are brought to front
      Object.values(guideLinesRef.current).forEach((guide) => {
        if (guide && canvas.getObjects().includes(guide)) {
          canvas.bringObjectToFront(guide);
        }
      });
    },
    [
      guideLinesRef,
      interactingRef,
      updateObjectAlignmentGuides,
      updateSingleGuide,
    ]
  );

  // Update all guide positions when canvas dimensions change
  const updateAllGuidePositions = useCallback((canvas: fabric.Canvas) => {
    const canvasWidth = canvas.getWidth();
    const canvasHeight = canvas.getHeight();
    const canvasCenterX = canvasWidth / 2;
    const canvasCenterY = canvasHeight / 2;
    const overflowAmount = 20;

    // Update canvas edge guides with consistent stroke widths
    if (guideLinesRef.current.left) {
      guideLinesRef.current.left.set({
        x1: 0,
        y1: 0,
        x2: 0,
        y2: canvasHeight,
      });
    }
    if (guideLinesRef.current.right) {
      guideLinesRef.current.right.set({
        x1: canvasWidth,
        y1: 0,
        x2: canvasWidth,
        y2: canvasHeight,
      });
    }
    if (guideLinesRef.current.top) {
      guideLinesRef.current.top.set({
        x1: 0,
        y1: 0,
        x2: canvasWidth,
        y2: 0,
      });
    }
    if (guideLinesRef.current.bottom) {
      guideLinesRef.current.bottom.set({
        x1: 0,
        y1: canvasHeight,
        x2: canvasWidth,
        y2: canvasHeight,
      });
    }
    if (guideLinesRef.current.centerX) {
      guideLinesRef.current.centerX.set({
        x1: canvasCenterX,
        y1: 0,
        x2: canvasCenterX,
        y2: canvasHeight,
      });
    }
    if (guideLinesRef.current.centerY) {
      guideLinesRef.current.centerY.set({
        x1: 0,
        y1: canvasCenterY,
        x2: canvasWidth,
        y2: canvasCenterY,
      });
    }

    // Update object alignment guides - strokeWidth is set by createGuideLine
    // No need to override strokeWidth here as it's already set correctly

    // Update edge guides with overflow - strokeWidth is set by createGuideLine
    if (guideLinesRef.current.leftEdge) {
      guideLinesRef.current.leftEdge.set({
        x1: 0,
        y1: -overflowAmount,
        x2: 0,
        y2: canvasHeight + overflowAmount,
      });
    }
    if (guideLinesRef.current.rightEdge) {
      guideLinesRef.current.rightEdge.set({
        x1: canvasWidth,
        y1: -overflowAmount,
        x2: canvasWidth,
        y2: canvasHeight + overflowAmount,
      });
    }
    if (guideLinesRef.current.topEdge) {
      guideLinesRef.current.topEdge.set({
        x1: -overflowAmount,
        y1: 0,
        x2: canvasWidth + overflowAmount,
        y2: 0,
      });
    }
    if (guideLinesRef.current.bottomEdge) {
      guideLinesRef.current.bottomEdge.set({
        x1: -overflowAmount,
        y1: canvasHeight,
        x2: canvasWidth + overflowAmount,
        y2: canvasHeight,
      });
    }

    // Bring all guides to front to ensure they appear above frames
    Object.values(guideLinesRef.current).forEach((guide) => {
      if (guide && canvas.getObjects().includes(guide)) {
        canvas.bringObjectToFront(guide);
      }
    });

    canvas.requestRenderAll();
  }, []);

  // Remove all guides from canvas
  const removeAllGuides = (canvas: fabric.Canvas) => {
    Object.values(guideLinesRef.current).forEach((guide) => {
      if (guide && canvas.getObjects().includes(guide)) {
        canvas.remove(guide);
      }
    });
    canvas.requestRenderAll();
  };

  return {
    updateGuideVisibility,
    removeAllGuides,
    updateAllGuidePositions,
  };
};

export default useGuideLineHook;
